import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";

/**
 * Read and write the autoscaler group of the admin settings document.
 *
 * The off-hours window and the concurrency ceiling live beside each other under
 * `autoscaler`, so both sections share this one hook rather than fetching twice.
 * A save invalidates the query so both sections re-read the server's answer
 * rather than trusting the value the operator typed.
 */
const apiUrl = process.env.REACT_APP_BACKEND_URL;

export type OffHoursWindow = { start: string; end: string };

export type AdminSettings = {
  pause_settings?: Record<string, boolean>;
  autoscaler?: { off_hours?: OffHoursWindow; concurrency?: number };
};

export const ADMIN_SETTINGS_QUERY_KEY = ["admin", "admin-settings"] as const;

function authHeaders(token: string) {
  return { Authorization: `Bearer ${token}` };
}

export function useAdminSettings() {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ADMIN_SETTINGS_QUERY_KEY,
    queryFn: async (): Promise<AdminSettings> => {
      const res = await axios.get(`${apiUrl}/api/v1/admin/admin-settings`, {
        headers: authHeaders(token!),
      });
      return res.data;
    },
    enabled: Boolean(token),
    staleTime: 30_000,
  });

  /**
   * Send a whole PATCH body, exactly as the payload builders produce it.
   *
   * Deliberately not wrapped in `autoscaler` here: the builders already emit
   * `{ autoscaler: ... }`, and wrapping a second time sends
   * `{autoscaler:{autoscaler:...}}`, which the API accepts with a 200 and
   * silently stores nothing.
   */
  const patch = async (body: Record<string, unknown>) => {
    const res = await axios.patch(`${apiUrl}/api/v1/admin/admin-settings`, body, {
      headers: authHeaders(token!),
    });
    // Re-read rather than seeding the cache from the response, so the saved value
    // shown is the one the server actually stored.
    await queryClient.invalidateQueries({ queryKey: ADMIN_SETTINGS_QUERY_KEY });
    return res.data as AdminSettings;
  };

  return { ...query, patch };
}